import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import React, { useState } from "react";
import { dateToDMY, getDateOffsetByDays } from "../../utils/dateUtils";
import { Calendar } from "primereact/calendar";
import { useGet } from "../../network/useGet";
import NetworkWrapper from "../NetworkWrapper";
import BaseTableView from "../cmsbase/BaseTableView";
import { excursionBookingTableFields } from "../../network/mockData";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Tooltip } from "primereact/tooltip";
import { postToHub } from "../../network/actions";
import { InputText } from "primereact/inputtext";
import { useNotificationContext } from "../../context/NotificationContext";
import { Dialog } from "primereact/dialog";
import ActionButtons from "./ActionButtons";

const INITIAL_FORM_DATA = {
  state: "Overbooked",
  syncStatus: "",
  accountNumber: null,
  cabinNumber: null,
  bookingCode: "",
  fromDate: getDateOffsetByDays(0), 
  toDate: getDateOffsetByDays(7),
  lastName: "",
  excursionName: ""
};

const BOOKING_STATE_OPTIONS = [
  { name: "All", value: "" },
  { name: "Ok", value: "Ok" },
  { name: "Failed", value: "Failed" },
  { name: "Overbooked", value: "Overbooked" },
  { name: "Cancelled", value: "Cancelled" },
  { name: "Overbook Confirmation", value: "OverbookConfirmation" },
];

const STATUS_OPTIONS = [
  { name: "All", value: "" },
  { name: "Synced", value: "Synced" },
  { name: "Not Synced", value: "NotSynced" },
];

const CONFIRM = "Confirm";
const CANCEL = "Cancel"; 

const ExcursionBookings = () => {

  const [filterString, setFilterString] = useState(
    `state=${INITIAL_FORM_DATA.state}&fromDate=${dateToDMY(INITIAL_FORM_DATA.fromDate)}&toDate=${dateToDMY(
      INITIAL_FORM_DATA.toDate
    )}`
  );

  const [reloadCount, setReloadCount] = useState(0);
  const [dialogInfo, setDialogInfo] = useState({
    visible: false,
    bulkAction: "",
    failedReferences: []
  });

  const {toast} = useNotificationContext();

  const url = "onboardBooking/part/search?" + filterString;

  const { isLoading, error, data } = useGet(url, reloadCount);

  const getBookingIdsForBulkAction = (bulkAction) => {
    return data
      .filter(({ state }) => (
        bulkAction === CONFIRM ? state === "Overbooked"
        : bulkAction === CANCEL ? (state === "Overbooked" || state === "Ok" || state === "OverbookConfirmation")
        : null
      ))
      .map((booking) => booking.reference);
  };

  const updateBookingsData = (data, bulkAction) => {

    const failedReferences = data[bulkAction===CONFIRM ? "failedConfirmationReference": "failedCancellationsReference"]
    if(failedReferences.length > 0){
      setDialogInfo({...dialogInfo, visible: true, bulkAction, failedReferences})
    }

    setReloadCount((reloadCount) => reloadCount + 1);
  };

  const commonFailureToast = () => {
    toast.current.show({severity: 'error', summary: 'Operation Failed'});
  }

  const confirmAll = () => {
    const bookings = getBookingIdsForBulkAction(CONFIRM)
    if(bookings.length > 0){
      confirmDialog({
        message: `You are about to Confirm ${bookings.length} ${bookings.length > 1 ? "Bookings" : "Booking"}`,
        header: "Confirmation",
        icon: "pi pi-exclamation-triangle",
        accept: async () => {
          const payload = {
            reference: bookings,
          };
          await postToHub("excursions/confirmAll", payload, (data)=> { updateBookingsData(data, CONFIRM) }, commonFailureToast);
        },
      });
    }else{
      toast.current.show({severity: 'warn', summary: "No Bookings found to Confirm"})
    }
  };

  const cancelAll = () => {
    const bookings = getBookingIdsForBulkAction(CANCEL)
    if(bookings.length > 0){
      confirmDialog({
        message: `You are about to Cancel ${bookings.length} ${bookings.length > 1 ? "Bookings" : "Booking"}`,
        header: "Confirmation",
        acceptClassName: "p-button-danger",
        icon: "pi pi-info-circle",
        accept: async () => {
          const payload = {
            reference: bookings,
          };
          await postToHub("excursions/cancelAll", payload, (data)=> { updateBookingsData(data, CANCEL) }, commonFailureToast);
        },
      });
    }else{
      toast.current.show({severity: 'warn', summary: "No Bookings found to Cancel"})
    }
  };

  const customColumn = (key) => {
    switch (key) {
      case "action":
        return <ActionButtons setReloadCount={setReloadCount}/>
    
      default:
        return <p>{`Implement the case for ${key} in ExcursionBookings.js`}</p>;
    }
  }

  return (
    <div className="max-w-full">
      <div className="flex  gap-4 ">
        <Button
          label="Confirm All"
          icon="pi pi-check"
          disabled={isLoading}
          onClick={confirmAll}
        />
        <Button
          label="Cancel All"
          icon="pi pi-times"
          className="p-button-danger"
          disabled={isLoading}
          onClick={cancelAll}
        />
      </div>
      <FilterForm
        setFilterString={setFilterString}
        isLoading={isLoading}
        setReloadCount={setReloadCount}
      />
      <NetworkWrapper isLoading={isLoading} error={error}>
        {data && (
          <div>
            <BaseTableView
              data={data}
              tableFields={excursionBookingTableFields}
              globalFilter={true}
              emptyMessage="No Bookings found for the Selected Criteria."
              customColumn={customColumn}
            />
          </div>
        )}
      </NetworkWrapper>
      <ConfirmDialog />
      <Dialog
        header="Information"
        visible={dialogInfo.visible}
        style={{ width: "50vw" }}
        onHide={() => setDialogInfo({...dialogInfo, visible:false})}
        modal
      >
        <div className="flex">
          <i className="pi pi-info-circle p-mr-2 mr-3"  style={{'fontSize': '2em'}} ></i>
          <span>Failed to {dialogInfo.bulkAction} the bookings for {dialogInfo.failedReferences.length} passengers</span>
        </div>
      </Dialog>
    </div>
  );
};

const FilterForm = ({
  setFilterString,
  isLoading,
  setReloadCount,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const onChangeFormField = (event) => {
    const name = event.target ? event.target.name : event.originalEvent.target.name;
    const value = event.target ? event.target.value : event.value;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const updateFilterString = (formData) => {

    const urlParams = Object.entries(formData)
    .filter(([key, value]) => !!value)
    .map(([key, value]) => `${key}=${value instanceof Date ? dateToDMY(value) : value}`)
    .join('&');

    setFilterString(urlParams);
    setReloadCount((reloadCount) => reloadCount + 1);
  };

  const onReset = () => {
    setFormData(INITIAL_FORM_DATA);
    updateFilterString(INITIAL_FORM_DATA);
  };

  return (
    <div className="px-4 py-8 border-b-2 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
      {/* bookingState */}
      <div>
        <label htmlFor="state">Booking State</label>
        <div>
          <Dropdown
            id="state"
            name="state"
            value={formData.state}
            onChange={onChangeFormField}
            options={BOOKING_STATE_OPTIONS}
            optionLabel="name"
            optionValue="value"
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Status */}
      <div>
        <label htmlFor="syncStatus">
          <span className="pr-2">Sync Status</span>
          <Tooltip target="#sync-status-info" />
          <i
            className="pi pi-info-circle"
            id="sync-status-info"
            data-pr-tooltip="Sync status for Excursion bookings with the cloud hub."
          />
        </label>
        <div>
          <Dropdown
            id="syncStatus"
            name="syncStatus"
            value={formData.syncStatus}
            onChange={onChangeFormField}
            options={STATUS_OPTIONS}
            optionLabel="name"
            optionValue="value"
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* account ID */}
      <div>
        <label htmlFor="accountNumber">Account Number</label>
        <div>
          <InputNumber
            useGrouping={false}
            id="accountNumber"
            name="accountNumber"
            value={formData.accountNumber}
            onChange={onChangeFormField}
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
          />
        </div>
      </div>

      {/*cabin Number*/}
      <div>
        <label htmlFor="cabinNumber">Cabin Number</label>
        <div>
          <InputNumber
            useGrouping={false}
            id="cabinNumber"
            name="cabinNumber"
            value={formData.cabinNumber}
            onChange={onChangeFormField}
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
          />
        </div>
      </div>

      {/*Excursion Booking Code*/}
      <div>
        <label htmlFor="bookingCode">Excursion Booking Code</label>
        <div>
          <InputText
            id="bookingCode"
            name="bookingCode"
            value={formData.bookingCode}
            onChange={onChangeFormField}
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* From date */}
      <div>
        <label htmlFor="date-from-input">
          <span className="pr-2">Excursion Date from</span>
          <Tooltip target="#date-from-input-info" />
          <i
            className="pi pi-info-circle"
            id="date-from-input-info"
            data-pr-tooltip="Excursion Date"
          />
        </label>
        <div>
          <Calendar
            inputId="date-from-input"
            name="fromDate"
            dateFormat="dd/mm/yy"
            value={formData.fromDate}
            onChange={onChangeFormField}
            className="w-9/12 max-w-9/12"
            minDate={new Date()}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* toDate */}
      <div>
        <label htmlFor="date-to-input">
          <span className="pr-2">Excursion Date to</span>
          <Tooltip target="#date-to-input-info" />
          <i
            className="pi pi-info-circle"
            id="date-to-input-info"
            data-pr-tooltip="Excursion Date"
          />
        </label>
        <div>
          <Calendar
            inputId="date-to-input"
            name="toDate"
            dateFormat="dd/mm/yy"
            value={formData.toDate}
            onChange={onChangeFormField}
            className="w-9/12 max-w-9/12"
            minDate={new Date(formData.fromDate)}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Last Name */}
      <div>
        <label htmlFor="lastName">Last Name</label>
        <div>
          <InputText
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={onChangeFormField}
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Excursion Name */}
      <div>
        <label htmlFor="excursionName">Excursion Name</label>
        <div>
          <InputText
            id="excursionName"
            name="excursionName"
            value={formData.excursionName}
            onChange={onChangeFormField}
            className="w-9/12 max-w-9/12"
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="flex gap-2 justify-start items-end md:col-span-2">
        {/* Get Data */}
        <Button
          label="Get Bookings"
          onClick={() => updateFilterString(formData)}
          disabled={isLoading}
        />

        {/* Reset */}
        <Button label="Reset" onClick={onReset} disabled={isLoading} />
      </div>
    </div>
  );
};

export default ExcursionBookings;
